import {getId} from 'apis/model/base'
import classNames from 'classnames'
import getTitle from 'helpers/getTitle'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useMemo, useState} from 'react'
import {GradeStudentReport} from 'views/AppLocals/Course/CourseClass/tabs/CourseClassGradeTab'
import {Button} from "antd";
import {AttendanceActionTypes, GradeActionTypes} from "../../../../AppLocals/Course";
import usePaginationHelper, {createPagerKey} from "../../../../AppLocals/hooks/usePaginationHelper";
import {NullAsDefault} from "../../../../Shared";
import {course_class_getGrades_Api} from "../../../../../apis";
import {bindQueryParam, bindQueryParams} from "../../../../AppLocals/functions/routerHelper";
import {renderCourseClassGrade} from "../../functions/renderCourseClassGrade";
import {DateNameSortBy} from "../../../../AppLocals/factory/createEntitySortBy";
import CouseClassGradeFilter from "../../components/filters/CouseClassGradeFilter";
import {useCourseGrades} from "../../../../AppLocals/Course/functions/useCourseGrades";
import createContextMenu from "../../../../AppLocals/factory/createContextMenu";
import {RiSearchEyeLine} from "react-icons/ri";
import {BiSolidEdit, BiTrash} from "react-icons/bi";
import {TbClockEdit} from "react-icons/tb";
import {BsThreeDots} from "react-icons/bs";
import {LayoutContext} from "../../../../../components/layouts/Default/LayoutContext";

export const CourseClassGradingPanel = ({item}) => {
    const t = useTranslate()
    const {isLg} = useContext(LayoutContext)
    const course_class_id = getId(item)
    const classSubjects = useMemo(
        () => (item?.class_subjects ?? []).filter(cs => !!cs?.subject),
        [item]
    )
    const [selectedSubjectId, setSelectedSubjectId] = useState(
        () => String(getId(classSubjects[0]?.subject) ?? '') || null
    )
    const selectedCs = useMemo(
        () => classSubjects.find(cs => String(getId(cs.subject)) === String(selectedSubjectId)),
        [classSubjects, selectedSubjectId]
    )
    const passedScore = selectedCs?.passed_score ?? 0

    const {
        modal,
        refresh_token,
        handleAction = NullAsDefault,
    } = useCourseGrades({course_class: item})

    const owner_id = item?.owner?.edit
    const owner_type = item?.owner?._type

    const renderContextMenu = useCallback(
        (item) =>
            item?.edit
                ? createContextMenu(
                [
                    {
                        icon: <RiSearchEyeLine size={14} className="text-green-600"/>,
                        label: 'view grades',
                        key: GradeActionTypes.GRADE_DETAIL,
                    },
                    {
                        icon: <BiSolidEdit size={14} className="text-blue-500"/>,
                        label: 'edit',
                        key: GradeActionTypes.GRADE_UPDATE,
                    },
                    {
                        icon: <TbClockEdit size={14} className="text-color-400"/>,
                        label: 'history',
                        key: GradeActionTypes.GRADE_HISTORY,
                    },
                    {
                        icon: <BiTrash size={14} className="text-primary-300"/>,
                        label: 'delete',
                        key: GradeActionTypes.GRADE_DELETE,
                    },
                ].filter((e) => item && !!!e?.invisible),
                (key) => {
                    handleAction(key, item)
                },
                {
                    icon: (
                        <div
                            style={{width: 22, height: 22, borderRadius: 999}}
                            className="flex flex-center background hover:background-200 text-color-500 hover:text-color-100 cursor-pointer">
                            <BsThreeDots size={12}/>
                        </div>
                    ),
                }
                )
                : undefined,
        [item?.edit]
    )

    const [pager_widget] = usePaginationHelper({
        filter_params: {
            owner_id,
            owner_type,
        },
        onInit: ({onSortChange = NullAsDefault, onFilterChange = NullAsDefault}) => {
            onFilterChange({subject_id: selectedSubjectId})
            // onSortChange(sortBy)
        },
        pagerInfo: {
            PaginationMeta: NullAsDefault,
            RenderEntity: React.Fragment,
            apiInfo: course_class_getGrades_Api,
            query: bindQueryParam({id: course_class_id}),
            renderItem: renderCourseClassGrade({
                renderContextMenu,
                handleAction,
            }),
            renderWrap: (child) => (
                <div className="flex flex-col divide-y divide-gray-200 list-item-workspace">
                    {child}
                </div>
            )
        },
        renderSearchBar: NullAsDefault,
        SortByWidget: DateNameSortBy,
        query_params: bindQueryParams([{subject_id: selectedSubjectId}]),
        sortHeader: NullAsDefault,
        withKey: createPagerKey(refresh_token),
        dependencies: [selectedSubjectId],
        AdvancedFilter: NullAsDefault,
        renderFilter: NullAsDefault,
        noHeader: true,
        renderHeader: NullAsDefault,
    })

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-x-4 gap-y-2">
                <div className="flex flex-col flex-1">
                    <div className="font-bold text-base text-slate-600 tracking-wide">
                        {t('grades')}
                    </div>
                    <div style={{ height: 3 }} className="my-1 w-20 h-1 rounded-full bg-gray-400"/>
                </div>
                <Button
                    style={{padding: isLg ? "0 18px" : "0 12px"}}
                    color="primary" variant="solid"
                    className="rounded-lg no-border flex flex-center font-medium text-xs md:text-sm"
                    onClick={() => handleAction(GradeActionTypes.GRADE_CREATE)}>
                    {t('create')}
                </Button>
            </div>
            {classSubjects.length > 0 && (
                <div className="flex flex-col gap-1.5">
                    <span className="text-color-300 tracking-wider">
                        {`${t('subjects')} :`}
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                        {classSubjects.map((cs) => {
                            const sid = String(getId(cs.subject))
                            const isActive = sid === String(selectedSubjectId)
                            const ps = cs.passed_score
                            return (
                                <button
                                    key={sid}
                                    onClick={() => setSelectedSubjectId(sid)}
                                    className={classNames(
                                        'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors select-none',
                                        isActive
                                            ? 'bg-indigo-600 border-indigo-600 text-white'
                                            : 'border-color-200 text-color-300 hover:border-indigo-400 hover:text-indigo-500'
                                    )}>
                                    <span>{getTitle(cs.subject)}</span>
                                    {ps > 0 && (
                                        <span className={classNames('text-2xs font-bold', isActive ? 'text-indigo-200' : 'text-slate-400')}>
                                            {`≥${ps}`}
                                        </span>
                                    )}
                                </button>
                            )
                        })}
                    </div>
                </div>
            )}
            <GradeStudentReport
                key={selectedSubjectId}
                course_class_id={course_class_id}
                activeSubjectId={selectedSubjectId}
                passedScore={passedScore}
            />
            <div className="flex flex-col gap-3">
                <div className="flex flex-col flex-1">
                    <div className="font-bold text-base text-slate-600 tracking-wide">
                        {t('list of lesson')}
                    </div>
                    <div style={{ height: 3 }} className="my-1 w-20 h-1 rounded-full bg-gray-400"/>
                </div>
                {pager_widget}
            </div>

            {modal}
        </div>
    )
}
